import { useRef, useState } from 'react';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useRouter } from '@tanstack/react-router';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useModals, useSnackbars } from '@customerio/pluma-components/react';
import { parseAsString, useQueryStates } from 'nuqs';
import { canIdentifyBy, IdentifierKey, isEmailIdentified, isMultiIdentifier } from '@/features/people';
import { getUserFacingErrorMessage } from '@/services/api/errors';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { customerKeys, type Customer } from '@/services/db/customer/entity';
import { mergeRequestBody, useMergeCustomers, useMergePreviewOptional } from '@/services/db/customer/merge';
import { getCustomer } from '@/services/db/customer/queries';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { generatePath } from '@/services/router/generate-path';
import { useNavigationHistory } from '@/services/router/navigation-history';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { snackbarApi } from '@/services/snackbar';
import { useMergeAnalytics } from './analytics';
import { MergeFeedbackModal } from './components/merge-feedback-modal';
import { allConfirmed } from './components/merge-actions';
import {
  ProfileType,
  Step,
  UNCONFIRMED,
  type MergeConfirmations,
  type MergeWorkspaceContext,
  type SelectableProfileType,
  type SelectedCustomers,
  type WizardStep,
} from './types';
import { previousUrlFrom, resolveCancelTarget } from './utils/cancel-target';

/** How long the "merging" confirmation stays up. */
const MERGE_SNACKBAR_TIMEOUT_MS = 10_000;

const MERGE_STARTED_MESSAGE = 'The profiles are now merging. This can take a few seconds to complete.';
const MERGE_FAILED_MESSAGE = "The profiles couldn't be merged. Try again.";
const CUSTOMER_STALE_TIME = 60_000;

/** The wizard's own search params: the selected pair, plus the attribution an inbound link carries. */
const SEARCH_PARAMS = {
  primary_id: parseAsString,
  secondary_id: parseAsString,
  source: parseAsString,
};

/**
 * The workspace facts and product words the cards read. `null` until the
 * environment has loaded.
 */
export function useMergeWorkspaceContext(workspaceId: number): MergeWorkspaceContext | null {
  const environment = useEnvironmentOptional(workspaceId);
  const withGravatar = useFeatureFlag(AccountFeatureFlag.GravatarEnabled);
  const router = useRouter();

  const record = environment.data?.environment;
  if (record == null) {
    return null;
  }

  return {
    identifiers: record.identifiers,
    isMultiIdentifier: isMultiIdentifier(record),
    isEmailIdentified: isEmailIdentified(record),
    canIdentifyByPhone: canIdentifyBy(IdentifierKey.Phone, record.identifiers),
    withGravatar,
    identifiersSettingsHref: router.buildLocation({
      to: '/workspaces/$workspaceId/settings/edit',
      params: { workspaceId: String(workspaceId) },
    }).href,
    terms: {
      peopleSingular: 'person',
      peoplePlural: 'people',
      peopleTitlePlural: 'People',
      customersSingular: 'profile',
      campaignsSingular: 'automation',
    },
  };
}

/** A person by id, or an inactive query when no id is selected. */
function useSelectedCustomer(workspaceId: number, customerId: string | null) {
  return useQuery({
    queryKey: customerId != null ? customerKeys.detail(workspaceId, customerId) : ['customer', 'detail', 'inactive'],
    queryFn: customerId != null ? () => getCustomer(workspaceId, customerId) : skipToken,
    staleTime: CUSTOMER_STALE_TIME,
  });
}

function emptyCardKey(type: SelectableProfileType): string {
  return `_${type}_empty`;
}

/** The card key for a slot: the selected id, or the slot's empty marker. */
function cardKeyFor(type: SelectableProfileType, customerId: string | null): string {
  return customerId ?? emptyCardKey(type);
}

/** The loaded person for a slot, or `null` while the slot is empty or still loading. */
function selectedFor(customerId: string | null, query: { data: Customer | undefined }): Customer | null {
  return customerId != null ? (query.data ?? null) : null;
}

/** Whether a slot has an id whose person has not loaded yet. */
function isSlotLoading(customerId: string | null, query: { isPending: boolean }): boolean {
  return customerId != null && query.isPending;
}

const SEARCH_PARAM_FOR = {
  primary: 'primary_id',
  secondary: 'secondary_id',
} as const satisfies Record<SelectableProfileType, keyof typeof SEARCH_PARAMS>;

/**
 * The pair being merged. The URL is the source of truth (`primary_id` /
 * `secondary_id`, internal ids); the people themselves are read through the
 * customer cache, which a selection from search seeds so the card renders
 * without a second request.
 */
function useSelectedPair(workspaceId: number) {
  const [params, setParams] = useQueryStates(SEARCH_PARAMS);
  const primaryId = params.primary_id;
  const secondaryId = params.secondary_id;
  const qc = useQueryClient();

  const primaryQuery = useSelectedCustomer(workspaceId, primaryId);
  const secondaryQuery = useSelectedCustomer(workspaceId, secondaryId);

  // Which card instance sits in which slot. Swapping exchanges the keys, so a
  // card keeps its search state (mode, results) when it changes sides.
  const [cardKeys, setCardKeys] = useState<Record<SelectableProfileType, string>>({
    primary: cardKeyFor(ProfileType.Primary, primaryId),
    secondary: cardKeyFor(ProfileType.Secondary, secondaryId),
  });

  const selected: SelectedCustomers = {
    primary: selectedFor(primaryId, primaryQuery),
    secondary: selectedFor(secondaryId, secondaryQuery),
  };

  const selectCustomer = (type: SelectableProfileType, customer: Customer | null) => {
    const customerId = customer?.id ?? null;
    if (customer != null) {
      qc.setQueryData(customerKeys.detail(workspaceId, customer.id), customer);
    }
    setCardKeys((keys) => ({ ...keys, [type]: cardKeyFor(type, customerId) }));
    void setParams({ [SEARCH_PARAM_FOR[type]]: customerId });
  };

  const swapCustomers = () => {
    setCardKeys((keys) => ({ primary: keys.secondary, secondary: keys.primary }));
    void setParams({ primary_id: secondaryId, secondary_id: primaryId });
  };

  return {
    primaryId,
    secondaryId,
    source: params.source,
    selected,
    cardKeys,
    /** A selected id whose person has not loaded yet. */
    isLoading: isSlotLoading(primaryId, primaryQuery) || isSlotLoading(secondaryId, secondaryQuery),
    error: primaryQuery.error ?? secondaryQuery.error,
    selectCustomer,
    swapCustomers,
  };
}

/** Where Cancel goes: back to the list or profile the person came from, else the primary's profile, else the list. */
function useCancelHref(workspaceId: number, primaryId: string | null): string {
  const { history } = useNavigationHistory();
  const { pathname } = useLocation();
  const target = resolveCancelTarget(previousUrlFrom(history, pathname), primaryId);
  switch (target.kind) {
    case 'url':
      return target.url;
    case 'person':
      return generatePath('/workspaces/:workspaceId/journeys/people/:personId', {
        workspaceId,
        personId: target.personId,
      });
    case 'people-list':
      return generatePath('/workspaces/:workspaceId/people', { workspaceId });
    default: {
      const _exhaustive: never = target;
      return _exhaustive;
    }
  }
}

/**
 * What the preview endpoint needs about the selected pair, or `null` while
 * either side is still loading. The unsubscribe states and the secondary's
 * internal id decide whether a secondary-only unsubscribe is carried over.
 */
function previewPairFor(selected: SelectedCustomers) {
  const { primary, secondary } = selected;
  if (primary == null || secondary == null) {
    return null;
  }
  return {
    primaryCustomerId: primary.id,
    secondaryCustomerId: secondary.id,
    primaryUnsubscribed: primary.unsubscribed,
    secondaryUnsubscribed: secondary.unsubscribed,
    secondaryCioId: secondary.identifiers?.cio_id ?? '',
  };
}

type UseConfirmAndMergeOptions = {
  workspaceId: number;
  primaryId: string | null;
  secondaryId: string | null;
  step: WizardStep;
  canMerge: boolean;
  hasRequiredSetup: boolean;
};

/**
 * Committing the merge. Every condition the commit requires is re-checked here
 * rather than trusted from the button, because Enter on the focused button and
 * a second click both reach this without the button's disabled state stopping
 * them. On success the confirmation is raised on the app-level snackbar API so
 * it outlives the exit to the profile page, which the embedded app serves.
 */
function useConfirmAndMerge({
  workspaceId,
  primaryId,
  secondaryId,
  step,
  canMerge,
  hasRequiredSetup,
}: UseConfirmAndMergeOptions) {
  const safeNavigate = useSafeNavigate();
  const snackbars = useSnackbars();
  const merge = useMergeCustomers({ workspaceId });
  const mergeBase = generatePath('/workspaces/:workspaceId/people/merge', { workspaceId });
  // Synchronous re-entry latch: two clicks (or a click and an Enter) landing
  // before the re-render both read a stale `isPending`.
  const isMergingRef = useRef(false);

  const isCommittable =
    canMerge && hasRequiredSetup && primaryId != null && secondaryId != null && step.id === Step.Preview
      ? allConfirmed(step.confirmations)
      : false;

  const confirmAndMerge = async () => {
    if (isMergingRef.current || !isCommittable || primaryId == null || secondaryId == null) {
      return;
    }
    isMergingRef.current = true;
    try {
      await merge.mutateAsync(mergeRequestBody({ primaryCustomerId: primaryId, secondaryCustomerId: secondaryId }));
      snackbarApi.create({
        id: 'people.merge.success',
        message: MERGE_STARTED_MESSAGE,
        autoCloseTimeout: MERGE_SNACKBAR_TIMEOUT_MS,
        action: {
          label: 'Merge another profile',
          href: `${mergeBase}?${new URLSearchParams({ source: 'merge_success_banner' }).toString()}`,
        },
      });
      // The profile page is served by the embedded app, so the exit goes through the handoff.
      safeNavigate(
        generatePath('/workspaces/:workspaceId/journeys/people/:personId', { workspaceId, personId: primaryId }),
      );
    } catch (error) {
      snackbars.create({ variant: 'critical', message: getUserFacingErrorMessage(error, MERGE_FAILED_MESSAGE) });
    } finally {
      isMergingRef.current = false;
    }
  };

  return { confirmAndMerge, isMerging: merge.isPending };
}

type UseMergeWizardOptions = {
  workspaceId: number;
  context: MergeWorkspaceContext | null;
};

/** Everything the merge page renders from and every action it offers. */
export function useMergeWizard({ workspaceId, context }: UseMergeWizardOptions) {
  const { hasPermission } = usePermissions();
  const canMerge = hasPermission('journeys/people/edit');
  const modals = useModals();

  const pair = useSelectedPair(workspaceId);
  const { primaryId, secondaryId, selected } = pair;
  const hasRequiredSetup = primaryId != null && secondaryId != null && primaryId !== secondaryId;
  const noCustomersSelected = selected.primary == null && selected.secondary == null;

  const [step, setStep] = useState<WizardStep>({ id: Step.Setup });
  const [searchTerm, setSearchTerm] = useState('');

  const preview = useMergePreviewOptional({
    workspaceId,
    pair: hasRequiredSetup ? previewPairFor(selected) : null,
  });
  const mergedAttributes = preview.data?.attributes ?? [];
  const mergeSummary = preview.data?.summary;

  const isReady = context != null && !pair.isLoading;
  const analytics = useMergeAnalytics(
    { step: step.id, selected, primaryId, secondaryId, mergeSummary, source: pair.source },
    isReady,
  );

  const changeSearchTerm = (term: string) => {
    setSearchTerm(term);
    analytics.trackAttributeSearch(term);
  };

  const selectCustomer = (type: SelectableProfileType, customer: Customer | null) => {
    pair.selectCustomer(type, customer);
    // With nobody selected the attribute search disappears, so a stale term must not filter the next card.
    const other = type === ProfileType.Primary ? selected.secondary : selected.primary;
    if (customer == null && other == null) {
      setSearchTerm('');
    }
  };

  const swapCustomers = () => {
    pair.swapCustomers();
    analytics.trackSwap();
  };

  const changeStep = (next: Step) => {
    analytics.trackStepChange(next);
    setStep(next === Step.Preview ? { id: Step.Preview, confirmations: UNCONFIRMED } : { id: Step.Setup });
  };

  /** Next: enter the review step and ask for a fresh preview. */
  const previewAndGoToNextStep = () => {
    changeStep(Step.Preview);
    void preview.refetch();
  };

  /** Back: return to setup, refreshing the preview for the cards' annotations. */
  const goToSetupStep = () => {
    changeStep(Step.Setup);
    if (hasRequiredSetup) {
      void preview.refetch();
    }
  };

  const setConfirmations = (confirmations: MergeConfirmations) => {
    setStep((current) => (current.id === Step.Preview ? { ...current, confirmations } : current));
  };

  const { confirmAndMerge, isMerging } = useConfirmAndMerge({
    workspaceId,
    primaryId,
    secondaryId,
    step,
    canMerge,
    hasRequiredSetup,
  });

  const showMergeFeedbackModal = () => {
    analytics.trackFeedbackRequest();
    modals.open(
      MergeFeedbackModal,
      { peoplePlural: context?.terms.peoplePlural ?? 'people' },
      { shouldCloseOnUnmount: true },
    );
  };

  const cancelHref = useCancelHref(workspaceId, primaryId);

  return {
    canMerge,
    isReady,
    error: pair.error,
    selected,
    cardKeys: pair.cardKeys,
    hasRequiredSetup,
    noCustomersSelected,
    step,
    searchTerm,
    mergedAttributes,
    mergeSummary,
    isPreviewing: preview.isFetching,
    isMerging,
    cancelHref,
    changeSearchTerm,
    selectCustomer,
    swapCustomers,
    changeStep,
    previewAndGoToNextStep,
    goToSetupStep,
    setConfirmations,
    confirmAndMerge,
    showMergeFeedbackModal,
  };
}
